import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchFleetSearchDetails } from "./api";
import { queryKeys } from "../../../../lib/query";

/**
 * Fleet search hook backed by React Query.
 *
 * - Fetches fleet data with server-side filtering
 * - Filters are passed as parameters and sent to the API
 * - Uses queryKeys.fleet.list(filters) to cache different filter combinations
 *
 * Returns the same shape as the old reducer: { state: { isLoading, isError, data }, refetch }
 */
function useFleetSearchReducer(filters = {}) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.fleet.list(filters), // Cache key includes filters
    queryFn: async () => {
      const result = await fetchFleetSearchDetails(filters); // Pass filters to API
      return result?.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const state = useMemo(
    () => ({
      isLoading,
      isError,
      data: data ?? [],
    }),
    [data, isLoading, isError]
  );

  const refetch = useCallback(() => {
    // Refetch with current filters
    queryClient.invalidateQueries({ queryKey: queryKeys.fleet.list(filters) });
  }, [queryClient, filters]);

  return { state, refetch };
}

export default useFleetSearchReducer;
